import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type RootState from 'app-components/utils/redux-store/rootState';

export const LoadState = Object.freeze({
  UNDEFINED: 'UNDEFINED', // (1) client has never fetched data from the server and not fetching data now - show refresh button
  INITIAL_LOAD: 'INITIAL_LOAD', // (2) initial load in progress - show skeleton
  INITIAL_LOAD_ERROR: 'INITIAL_LOAD_ERROR', // (1) initial load failed - show 'sorry try again'
  EMPTY: 'EMPTY', // (3) data has been loaded, but server has empty data collection - show add/setup button
  EMPTY_UPDATE: 'EMPTY_UPDATE', // (2) incremental data update for empty data collection is in progress - show skeleton
  EMPTY_UPDATE_ERROR: 'EMPTY_UPDATE_ERROR', // (1) incremental data update for empty data collection failed - show 'try again'
  READY: 'READY', // (4) we have some data and it is up to date - show corresponding feature
  READY_UPDATE: 'READY_UPDATE', // (5) data is ready and is updating now - progress (optional)
  READY_UPDATE_ERROR: 'READY_UPDATE_ERROR', // (6) data is ready and update failed - try again (optional)
} as const);

export type LoadState = (typeof LoadState)[keyof typeof LoadState];

export type ResourceCollection = { readonly size: number };

const isResourceCollection = (resources: unknown): resources is ResourceCollection =>
  typeof resources === 'object' && resources !== null && 'size' in resources && typeof resources.size === 'number';

const getResourceSize = (resources: unknown): number | undefined =>
  isResourceCollection(resources) ? resources.size : undefined;

export type ResourceSelectors = {
  getLoadPending?: (state: RootState) => boolean | null | undefined;
  getIsLoading?: (state: RootState) => boolean | null | undefined;
  getError?: (state: RootState) => unknown;
  getResourcesById?: (state: RootState) => unknown;
};

export type LoadStates = {
  isNoData: boolean;
  isSkeleton: boolean;
  isReady: boolean;
  isLoading: boolean;
  isError: boolean;
};

export type LoadSetupStates = {
  isUpdateRequired: boolean;
  isEmpty: boolean;
  isSkeleton: boolean;
  isReady: boolean;
  isLoading: boolean;
  isError: boolean;
};

// presets
export const useEmptyState = (state: LoadState): boolean => state === LoadState.EMPTY;

// show 'error - try again' option
// client tried to fetch data, but no local data because of error
// it's important to explain to user that we might have the data on the server, but can't fetch it now
export const tryAgainStates: LoadState[] = [LoadState.INITIAL_LOAD_ERROR, LoadState.EMPTY_UPDATE_ERROR];

// (optional) show error linear progress
// some error occurred
export const errorStates: LoadState[] = [...tryAgainStates, LoadState.READY_UPDATE_ERROR];
export const useErrorState = (state: LoadState): boolean => useMemo(() => errorStates.includes(state), [state]);
// show 'load initial data' option (use when client setup required - like need to add account first to get transactions)
// data was never successfully loaded for some reason
// it's important to explain to user that we might have the data on the server
export const updateRequiredStates: LoadState[] = [...tryAgainStates, LoadState.UNDEFINED];
export const useUpdateRequiredState = (state: LoadState): boolean =>
  useMemo(() => updateRequiredStates.includes(state), [state]);
// show 'refresh' option' use when client setup not required
// no local data for any reason (error or server has no data)
export const noDataStates: LoadState[] = [...updateRequiredStates, ...tryAgainStates, LoadState.EMPTY];
export const useNoDataState = (state: LoadState): boolean => useMemo(() => noDataStates.includes(state), [state]);
// show 'skeleton' - no local data and load is in progress
export const skeletonStates: LoadState[] = [LoadState.INITIAL_LOAD, LoadState.EMPTY_UPDATE];
export const useSkeletonState = (state: LoadState): boolean => useMemo(() => skeletonStates.includes(state), [state]);
// (optional) show loading linear progress
export const loadingStates: LoadState[] = [...skeletonStates, LoadState.READY_UPDATE];
export const useLoadingState = (state: LoadState): boolean => useMemo(() => loadingStates.includes(state), [state]);
// show main feature - data is ready
export const readyStates: LoadState[] = [LoadState.READY, LoadState.READY_UPDATE, LoadState.READY_UPDATE_ERROR];
export const useReadyState = (state: LoadState): boolean => useMemo(() => readyStates.includes(state), [state]);

// Keeps an already-confirmed empty collection (its zero state) on screen while it refreshes, rather than
// a skeleton. Use where the empty result may have been restored from the local cache, like dashboard cards.
export const settleEmptyUpdate = (state: LoadState): LoadState =>
  state === LoadState.EMPTY_UPDATE ? LoadState.EMPTY : state;

export type LoadStatesOptions = { keepEmptyWhileUpdating?: boolean };

// recommended for most use cases
// isReady - (required) show main feature
// isNoData - (required) 'load/refresh' button
// isSkeleton - show skeleton instead of the data
// isLoading - optional non-blocking loading indicator
// isError - optional non-blocking error indicator
export const useLoadStates = (
  resourceSelectors: ResourceSelectors,
  resourcesOverride?: unknown,
  options?: LoadStatesOptions,
): LoadStates => {
  const rawLoadState = useLoadState(resourceSelectors, resourcesOverride);
  const loadState = options?.keepEmptyWhileUpdating ? settleEmptyUpdate(rawLoadState) : rawLoadState;
  const isNoData = useNoDataState(loadState);
  const isSkeleton = useSkeletonState(loadState);
  const isReady = useReadyState(loadState);
  // Still reports the background refresh as a non-blocking load.
  const isLoading = useLoadingState(rawLoadState);
  const isError = useErrorState(loadState);
  const loadStates = useMemo(
    () => ({ isNoData, isSkeleton, isReady, isLoading, isError }),
    [isNoData, isSkeleton, isReady, isLoading, isError],
  );
  return loadStates;
};

// true while any store's first load is outstanding and hasn't failed. Background refetches and
// refetches of an empty collection don't count (isLoading/isSkeleton do), so this is safe for UI
// that unmounts its content while pending; a failed first load stops blocking instead of hanging.
export type FirstLoadSelectors = Required<Pick<ResourceSelectors, 'getLoadPending'>> &
  Pick<ResourceSelectors, 'getError'>;

export const useFirstLoadPending = (...stores: FirstLoadSelectors[]): boolean =>
  useSelector((state: RootState) =>
    stores.some((store) => Boolean(store.getLoadPending(state)) && !store.getError?.(state)),
  );

// recommended when user suppose to initiate/setup a resource (like 'click to setup')
// isEmpty - (required) no data on server side - handled by init/setup ux (like 'click to add/setup')
// isReady - (required) data is ready - show main feature
// isUpdateRequired - show 'try again/update/refresh' button
// isSkeleton - show skeleton
// isLoading - optional non-blocking loading indicator
// isError - optional non-blocking error indicator
export const useLoadSetupStates = (
  resourceSelectors: ResourceSelectors,
  resourcesOverride?: unknown,
): LoadSetupStates => {
  const loadState = useLoadState(resourceSelectors, resourcesOverride);
  const isUpdateRequired = useUpdateRequiredState(loadState);
  const isEmpty = useEmptyState(loadState);
  const isSkeleton = useSkeletonState(loadState);
  const isReady = useReadyState(loadState);
  const isLoading = useLoadingState(loadState);
  const isError = useErrorState(loadState);
  const loadStates = useMemo(
    () => ({ isUpdateRequired, isEmpty, isSkeleton, isReady, isLoading, isError }),
    [isUpdateRequired, isEmpty, isSkeleton, isReady, isLoading, isError],
  );
  return loadStates;
};

// (useLoadStates is recommended to use instead useLoadState)
// resourceSelectors should contain following methods to be fully compatible with this hook
// getLoadPending
// getIsLoading
// getError
// getResourcesById
export function useLoadState(resourceSelectors: ResourceSelectors, resourcesOverride?: unknown): LoadState {
  let loadState: LoadState = LoadState.READY;

  // verify(resourceSelectors.getLoadPending, 'getLoadPending selector is not defined');
  const loadPending = useSelector((state: RootState) => resourceSelectors.getLoadPending?.(state));
  // verify(resourceSelectors.getIsLoading, 'getIsLoading selector is not defined');
  const isLoading = useSelector((state: RootState) => resourceSelectors.getIsLoading?.(state));
  // verify(resourceSelectors.getError, 'getError selector is not defined');
  const error = useSelector((state: RootState) => resourceSelectors.getError?.(state));
  // Only the size decides the state, so record refreshes don't re-render the caller.
  const allResourcesSize = useSelector((state: RootState) =>
    getResourceSize(resourceSelectors.getResourcesById?.(state)),
  );
  const resourceSize = resourcesOverride ? getResourceSize(resourcesOverride) : allResourcesSize;
  verify(resourceSize != null, 'resourcesOverride or getResourcesById selector does not return a valid collection');

  if (loadPending && !isLoading && !resourceSize && !error) loadState = LoadState.UNDEFINED;
  else if (loadPending && isLoading && !resourceSize) loadState = LoadState.INITIAL_LOAD;
  else if (loadPending && isLoading && resourceSize) loadState = LoadState.READY_UPDATE;
  else if (loadPending && !isLoading && !resourceSize && error) loadState = LoadState.INITIAL_LOAD_ERROR;
  else if (loadPending && !isLoading && resourceSize && !error) loadState = LoadState.READY;
  else if (!loadPending && !isLoading && resourceSize === 0 && !error) loadState = LoadState.EMPTY;
  else if (!loadPending && isLoading && resourceSize === 0) loadState = LoadState.EMPTY_UPDATE;
  else if (!loadPending && !isLoading && resourceSize === 0 && error) loadState = LoadState.EMPTY_UPDATE_ERROR;
  else if (!loadPending && !isLoading && resourceSize && !error) loadState = LoadState.READY;
  else if (!loadPending && isLoading && resourceSize) loadState = LoadState.READY_UPDATE;
  else if (!loadPending && !isLoading && resourceSize && error) loadState = LoadState.READY_UPDATE_ERROR;
  else {
    verify(
      false,
      `unexpected state loadPending=${loadPending} isLoading=${isLoading} error=${error} resources.size=${resourceSize}`,
    );
  }

  return loadState;
}
